import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { supabase, restoreMainAppSsoSession } from '../../services/supabase';
import { PUBLIC_TALENT_COLUMNS } from '../../constants/talentColumns';
import { readMyUser } from '../../services/myUser';

/**
 * Creator session for the redesigned workspace.
 *
 * This reproduces the identity ladder that BioDashboard.tsx runs in its boot
 * effect (roughly :891-1200). It is deliberately a SEPARATE implementation
 * rather than an edit to that file: the live dashboard keeps working exactly as
 * it does today, and the redesign gets a session layer shaped for many route
 * components instead of one giant one.
 *
 * The order below is not arbitrary — each step exists because of a specific
 * failure:
 *
 *  1. `?token=` is ambiguous. It is tried as a `talent_profiles.id` (admin
 *     opening a creator) and then as a `users.id` (a creator opening their own
 *     dashboard). Query-string identity is CONTEXT, never authorization.
 *  2. The bootstrap reads may run as `anon`, because no Supabase session
 *     necessarily exists yet. Post-2026-07-09 lockdown `anon` may only read the
 *     public column allow-list, so these reads MUST use PUBLIC_TALENT_COLUMNS —
 *     a `select('*')` here 403s.
 *  3. `getSession()` happily returns a cached user from an expired JWT, which
 *     made the old gate treat the app as authenticated while every write got a
 *     401. So the session is validated, refreshed once, and only then trusted.
 *  4. Full rows are re-fetched under `authenticated` AFTER a session exists.
 *     Payout and identity columns exist only on that second read — which is why
 *     `columnsAreFull` is exposed. A screen that renders payout onboarding state
 *     while this is false will tell a fully-onboarded creator to start over.
 *  5. A creator with no `bio_settings` row has half a workspace: AMA, ShoutOut
 *     Card, Newsletter and Publish are all disabled on a missing id. The row is
 *     created here, once, rather than by whichever screen noticed first.
 *  6. The person driving is not always the creator. A forwarded admin is
 *     resolved as a SEPARATE identity so chat posts under the right name.
 *
 * Everything runs ONCE, above the router. If each screen re-ran it, the
 * `verifyOtp` calls would race and replace each other's session, and the
 * `bio_settings` create would race itself.
 */

export type TalentProfileRow = Record<string, any> & { id: string; user_id?: string };
export type UserRow = Record<string, any> & { id: string };

/**
 * The staff member looking at this creator's workspace through an
 * impersonation link, resolved SEPARATELY from the creator.
 *
 * Conflating the two is how support ends up posting in #creators under the
 * creator's own name, permanently: chat picks its sender from whichever
 * identity it is handed, and the creator's row is the wrong one.
 */
export type ForwardedAdmin = {
  /**
   * The `users.id` forwarded on the link (or carried in `creator_session`).
   * CONTEXT, never authorization — `user` below is the verified part.
   */
  id: string | null;
  /** The row, and only when `users.user_type === 'admin'`. Null means not staff. */
  user: UserRow | null;
  /** Their email where known. The legacy @everyone allowlist is keyed on it. */
  email: string | null;
};

const NO_FORWARDED_ADMIN: ForwardedAdmin = { id: null, user: null, email: null };

export type CreatorSessionState = {
  status: 'loading' | 'ready' | 'needs-login' | 'error';
  profile: TalentProfileRow | null;
  user: UserRow | null;
  talentId: string | null;
  /**
   * Staff viewing this creator through an impersonation link. Anything that
   * WRITES as a person — chat, notes — must send this identity when it is set,
   * not `user`.
   */
  forwardedAdmin: ForwardedAdmin;
  /**
   * False while only the anon-safe column allow-list has loaded. Screens that
   * depend on payout/identity columns must render a skeleton, never a
   * "not set up" state, while this is false.
   */
  columnsAreFull: boolean;
  /** Prefilled into the login gate when we know who is signing in. */
  loginEmail: string | null;
  error: string | null;
  refresh: () => Promise<void>;
  /** Local optimistic patch. Does not write — call the mutation, then this. */
  patchProfile: (patch: Partial<TalentProfileRow>) => void;
};

const ANON_SAFE_USER_COLUMNS = 'id, full_name, avatar_url, user_type, created_at, updated_at';

const CreatorSessionContext = createContext<CreatorSessionState | null>(null);

/** The handoff blob every ShoutOut surface reads. Not a credential — a hint. */
export const CREATOR_SESSION_KEY = 'creator_session';

/**
 * The App Store reviewer's account. It must never see the community chat or the
 * paid-sponsor surface: user-generated content with no visible moderation is a
 * rejection, and the reviewer has no way to moderate anything.
 */
export const DEMO_ACCOUNT_EMAIL = 'applereview@shoutout.us';

export type StoredCreatorSession = { userId?: string; email?: string; talentId?: string };

export function readStoredSession(): StoredCreatorSession {
  try {
    return JSON.parse(localStorage.getItem(CREATOR_SESSION_KEY) || '{}');
  } catch {
    return {};
  }
}

/** Writes the handoff blob, dropping empty keys so a miss never lands as `undefined`. */
export function writeStoredSession(next: StoredCreatorSession): void {
  const value: StoredCreatorSession = {};
  if (next.userId) value.userId = next.userId;
  if (next.email) value.email = next.email;
  if (next.talentId) value.talentId = next.talentId;
  try {
    localStorage.setItem(CREATOR_SESSION_KEY, JSON.stringify(value));
  } catch {
    /* private mode — the session still works, it just will not survive a reload */
  }
}

/**
 * Is the signed-in identity the Apple review demo account?
 *
 * Same check the live dashboard makes (BioDashboard.tsx:452, :1427, :7533) and
 * the same one usePageContent already uses for the sponsor surface: the email
 * in the handoff blob, which is whoever signed in — not necessarily the creator
 * whose page is open, exactly as live behaves.
 */
export function isDemoAccountSession(): boolean {
  return (readStoredSession().email || '').trim().toLowerCase() === DEMO_ACCOUNT_EMAIL;
}

/**
 * Default `bio_settings` for a creator who has none, byte-for-byte the row the
 * live dashboard creates on every load (BioDashboard.tsx:1377-1394).
 *
 * These hexes are DATA — the fan page's own colours, stored in the database and
 * edited by the creator in Appearance. They are not workspace design tokens and
 * must not be swapped for any.
 *
 * Without this row `settings?.id` is null, and AMA, ShoutOut Card, Newsletter
 * and Publish are all disabled — the permanent state of every creator who has
 * never opened the live dashboard.
 */
function defaultBioSettings(profile: TalentProfileRow): Record<string, any> {
  return {
    talent_id: profile.id,
    theme: 'glass',
    background_color: '#0a0a0a',
    accent_color: '#3b82f6',
    font_family: 'Inter',
    show_shoutout_card: true,
    show_ask_me_anything: false,
    is_published: true,
    background_type: 'gradient',
    gradient_start: '#0a0a0a',
    gradient_end: '#1a1a2e',
    gradient_direction: 'to-b',
    button_style: 'rounded',
    button_color: '#3b82f6',
    text_color: '#ffffff',
    card_style: 'glass',
    card_opacity: 0.10,
    // Live reads the raw column here, NOT the temp_full_name/user fallback the
    // header shows. Matched deliberately so both surfaces seed the same row.
    display_name: profile.full_name,
    profile_image_url: profile.temp_avatar_url,
  };
}

/**
 * Ensure this creator HAS a `bio_settings` row.
 *
 * Runs only once a real session exists — `create_bio_settings` is an
 * `authenticated` RPC, and calling it as `anon` fails and leaves the creator in
 * exactly the state it was meant to fix.
 */
async function ensureBioSettings(profile: TalentProfileRow): Promise<void> {
  try {
    const { data: existing } = await supabase
      .from('bio_settings')
      .select('id')
      .eq('talent_id', profile.id)
      .maybeSingle();
    if (existing) return;

    await supabase.rpc('create_bio_settings', { p_settings: defaultBioSettings(profile) });
  } catch (e) {
    // Never fatal: a creator with no settings row still has orders, payouts and
    // an inbox. The My Page tabs stay disabled, which is today's behaviour.
    console.warn('Could not create default bio settings:', e);
  }
}

/**
 * A creator who signed in on this device has a Supabase session in localStorage
 * long after the `?token=` handoff has fallen off the URL. Returns the
 * `users.id` it belongs to, which the ladder below resolves like any token.
 *
 * `getSession()` alone is not enough — it happily returns a user parsed out of
 * an expired JWT — so it is validated, and refreshed once, before being
 * trusted.
 */
async function userIdFromStoredSupabaseSession(): Promise<string | null> {
  try {
    const { data: existing } = await supabase.auth.getSession();
    if (!existing?.session?.user) return null;

    const { data: validated, error: validationError } = await supabase.auth.getUser();
    if (!validationError && validated?.user) return validated.user.id;

    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (!refreshError && refreshed?.session?.user) return refreshed.session.user.id;
    return null;
  } catch {
    return null;
  }
}

export function CreatorSessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<CreatorSessionState['status']>('loading');
  const [profile, setProfile] = useState<TalentProfileRow | null>(null);
  const [user, setUser] = useState<UserRow | null>(null);
  const [columnsAreFull, setColumnsAreFull] = useState(false);
  const [loginEmail, setLoginEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forwardedAdmin, setForwardedAdmin] = useState<ForwardedAdmin>(NO_FORWARDED_ADMIN);

  // Guards against React 18/19 StrictMode double-invoking the effect, which
  // would fire two creator-session/verifyOtp exchanges in development.
  const bootRef = useRef(false);

  const bootstrap = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const stored = readStoredSession();
    let token = params.get('token') || stored.talentId || null;
    const urlEmail = params.get('email');
    const urlUserId = params.get('userId');

    /*
     * A stored `userId` is carried forward unconditionally, as live does: it is
     * how an admin STAYS an admin after signing in on a creator's link, where
     * the URL carries a `?token=` for someone else and no userId at all. It is
     * only ever a hint — `users.user_type` is verified below before it means
     * anything.
     *
     * The stored EMAIL is not carried that freely. It must describe the creator
     * actually open, because that email is what the demo-account and @everyone
     * checks read; opening creator B's order link on a device that last signed
     * creator A in would otherwise answer both questions about A. When the
     * forwarded user turns out to be staff, their real address is resolved from
     * the database anyway, so nothing is lost by being strict here.
     */
    const carriedUserId = stored.userId;
    const carriedEmail = stored.talentId && stored.talentId === token ? stored.email : undefined;

    // Admin links and post-impersonation redirects originate on shoutout.us.
    // Hydrate that verified HttpOnly SSO session before anything else.
    if (token && urlUserId) {
      try {
        await restoreMainAppSsoSession(urlUserId);
      } catch {
        /* non-fatal: we fall through to the session ladder below */
      }
    }

    /*
     * No token anywhere — but a creator who signed in here still has a Supabase
     * session in localStorage. Ask it who they are BEFORE giving up: this is
     * the ordinary state of a bookmarked workspace, and of Sign Out's own
     * landing page, which clears the blob on purpose.
     */
    if (!token) {
      token = await userIdFromStoredSupabaseSession();
    }

    if (!token) {
      // Not an error — nobody is signed in. The workspace renders its own login
      // form for this; anything else is a dead end with no way out of it.
      setLoginEmail(stored.email || null);
      setStatus('needs-login');
      return;
    }

    try {
      let resolvedProfile: TalentProfileRow | null = null;
      let resolvedUser: UserRow | null = null;
      /** The creator's own email, once anything has managed to resolve it. */
      let resolvedEmail: string | null = null;

      // Step 1 — token as a talent_profiles.id.
      const { data: directProfile } = await supabase
        .from('talent_profiles')
        .select(PUBLIC_TALENT_COLUMNS as '*')
        .eq('id', token)
        .single();

      if (directProfile) {
        resolvedProfile = directProfile as any;
        if ((directProfile as any).user_id) {
          const { data: userFromProfile } = await supabase
            .from('users')
            .select(ANON_SAFE_USER_COLUMNS)
            .eq('id', (directProfile as any).user_id)
            .single();
          resolvedUser = (userFromProfile as any) || null;
        }
      } else {
        // Step 2 — token as a users.id.
        const { data: userById } = await supabase
          .from('users')
          .select(ANON_SAFE_USER_COLUMNS)
          .eq('id', token)
          .single();

        if (!userById) {
          setError('Invalid authentication token. Try again from your ShoutOut dashboard.');
          setStatus('error');
          return;
        }
        resolvedUser = userById as any;

        const { data: profileByUser } = await supabase
          .from('talent_profiles')
          .select(PUBLIC_TALENT_COLUMNS as '*')
          .eq('user_id', (userById as any).id)
          .single();

        if (!profileByUser) {
          setError('No creator profile found for this account.');
          setStatus('error');
          return;
        }
        resolvedProfile = profileByUser as any;
      }

      /*
       * Step 2b — WHO IS DRIVING? Not necessarily the creator above.
       *
       * Staff open a creator's workspace through an impersonation link carrying
       * `?userId=<their own users.id>`. That id is resolved and verified here as
       * a separate identity, exactly as the live dashboard does
       * (BioDashboard.tsx:1002-1036), so chat can post as the admin. Deriving
       * "is an admin" from the CREATOR's row instead — which is what this file
       * used to do by omission — makes every support reply in #creators appear
       * under the creator's own name, permanently and unfixably.
       *
       * The id is only ever CONTEXT: `users.user_type` is what is trusted, and
       * the write policies behind chat re-check it server-side anyway.
       */
      const forwardedAdminId = urlUserId || carriedUserId || null;
      let adminRow: UserRow | null = null;
      let adminEmail: string | null = urlEmail || carriedEmail || null;

      if (forwardedAdminId) {
        // Anon-safe columns only: this can still run before a session exists.
        const { data: candidate } = await supabase
          .from('users')
          .select('id, user_type')
          .eq('id', forwardedAdminId)
          .maybeSingle();

        if ((candidate as any)?.user_type === 'admin') {
          adminRow = candidate as any;
          if (!adminEmail) {
            try {
              const { data: emailRows } = await supabase.rpc('lookup_user_email_by_id', {
                p_user_id: forwardedAdminId,
              });
              const resolved = Array.isArray(emailRows)
                ? emailRows[0]?.email
                : (emailRows as any)?.email;
              adminEmail = resolved || null;
            } catch {
              /* the allowlist check below just falls back to the Core Team tag */
            }
          }
        }
      }

      setForwardedAdmin({ id: forwardedAdminId, user: adminRow, email: adminEmail });

      // Step 3 — establish a genuinely usable session.
      const { data: existing } = await supabase.auth.getSession();
      let usableSession = existing?.session || null;

      if (usableSession?.user) {
        const { data: validated, error: validationError } = await supabase.auth.getUser();
        if (validationError || !validated.user) {
          const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
          usableSession = refreshError || !refreshed.session ? null : refreshed.session;
        }
      }

      const sessionUserId = usableSession?.user?.id;
      const targetUserId = resolvedProfile?.user_id;
      const matches = sessionUserId && targetUserId && sessionUserId === targetUserId;

      if (!matches) {
        // `carriedEmail`, not the raw blob: an email belonging to a DIFFERENT
        // creator would be sent to `creator-session` alongside this talent_id,
        // and it prefills the login form.
        let sessionEmail: string | null =
          (resolvedUser as any)?.email || urlEmail || carriedEmail || null;

        // `anon` cannot read users.email since the lockdown; the definer RPC is
        // the supported way to resolve it.
        if (!sessionEmail && targetUserId) {
          try {
            const { data: emailRows } = await supabase.rpc('lookup_user_email_by_id', {
              p_user_id: targetUserId,
            });
            const resolved = Array.isArray(emailRows)
              ? emailRows[0]?.email
              : (emailRows as any)?.email;
            if (resolved) sessionEmail = resolved;
          } catch {
            /* fall through to the login gate */
          }
        }

        if (sessionEmail && resolvedProfile?.id) {
          try {
            // `return_session`: creator-session is admin-only, so this is staff
            // opening someone else's workspace. Asking for the finished session
            // (rather than a token hash to exchange here) lets the function
            // record it as a staff view-as session first — the only way the
            // database can refuse its replies and read receipts in the Creator
            // Inbox. An older deploy still answers with `hashed_token`.
            const { data: csData } = await supabase.functions.invoke('creator-session', {
              body: { talent_id: resolvedProfile.id, email: sessionEmail, return_session: true },
            });
            const minted = (csData as any)?.session;
            if (minted?.access_token && minted?.refresh_token) {
              // Only drop the stale session once a replacement is in hand.
              if (sessionUserId) await supabase.auth.signOut();
              const { data: setData, error: setErr } = await supabase.auth.setSession({
                access_token: minted.access_token,
                refresh_token: minted.refresh_token,
              });
              if (!setErr) usableSession = setData.session;
            } else if ((csData as any)?.hashed_token) {
              if (sessionUserId) await supabase.auth.signOut();
              const { data: otpData, error: otpErr } = await supabase.auth.verifyOtp({
                token_hash: (csData as any).hashed_token,
                type: 'magiclink',
              });
              if (!otpErr) usableSession = otpData.session;
            }
          } catch {
            /* keep whatever session we had; the gate below decides */
          }
        }

        if (sessionEmail) {
          resolvedEmail = sessionEmail;
          setLoginEmail(sessionEmail);
        }
      }

      // Step 4 — re-read the full rows now that `authenticated` should apply.
      let full = false;
      if (usableSession?.user && resolvedProfile) {
        const { data: fullProfile } = await supabase
          .from('talent_profiles')
          .select('*')
          .eq('id', resolvedProfile.id)
          .single();
        if (fullProfile) {
          resolvedProfile = fullProfile as any;
          full = true;
        }
        // The full users row: only the signed-in creator's own, and only
        // through get_my_user() (the private columns aren't directly
        // readable since the users column lock). Staff viewing someone else
        // keep the anon-safe columns read above.
        const fullUserId = resolvedProfile?.user_id;
        if (fullUserId && fullUserId === usableSession.user.id) {
          const fullUser = await readMyUser<UserRow>('*');
          if (fullUser) resolvedUser = fullUser;
        }
      }

      if ((resolvedUser as any)?.email) resolvedEmail = (resolvedUser as any).email;

      setProfile(resolvedProfile);
      setUser(resolvedUser);
      setColumnsAreFull(full);

      /*
       * PERSIST THE HANDOFF — on any token, not only the fully-populated
       * `token+email+userId` link this used to require.
       *
       * Three live generators send fewer than three params: the "View & Fulfill
       * Order" email (token only) and both staff impersonation links (token +
       * userId). No NavLink in this app preserves `location.search`, so the
       * token is gone after the first tap and every one of those links gave a
       * creator exactly one screen before the workspace forgot who they were.
       *
       * Written HERE rather than on the way in, so only a token that actually
       * resolved is ever stored — a bad `?token=` would otherwise poison the
       * blob, and the blob is itself used as a token on the next load.
       *
       * `talentId` becomes the canonical profile id (the token may have been a
       * users.id), and email/userId are filled from the creator we resolved. A
       * forwarded `userId` is preserved rather than overwritten — it is the
       * ADMIN's, and losing it on the next reload would silently end the
       * impersonation while the link still looks live.
       */
      writeStoredSession({
        // A VERIFIED admin, or one the URL just forwarded (the verify read can
        // fail), outranks the creator. A stale id carried from a previous
        // creator does not — it is replaced here, so the blob heals itself.
        userId: adminRow?.id || urlUserId || resolvedProfile?.user_id,
        // userId and email must describe the SAME person — whoever is driving.
        // Half an admin and half a creator in one blob is how the demo-account
        // and @everyone checks, which read this email, end up answering about
        // someone who is not here.
        email:
          (adminRow ? adminEmail : null) ||
          urlEmail ||
          carriedEmail ||
          resolvedEmail ||
          undefined,
        talentId: resolvedProfile?.id || token,
      });

      if (!usableSession?.user) {
        setLoginEmail((prev) => prev || resolvedEmail || null);
        setStatus('needs-login');
        return;
      }

      /*
       * THE OWNER IS THE CREATOR. A staff login (users.user_type = 'admin')
       * that owns this page, e.g. Nicky on @nicky, is the creator here, not
       * staff viewing someone: its own id is carried in the stored blob and
       * would otherwise be verified above as a "forwarded admin". Decided only
       * from the VERIFIED session user (not the URL or the stored hint) being
       * both the page's owner and that admin. Staff on someone else's page keep
       * their forwarded identity, and a recorded view-as session (even of an
       * admin-owned page) is still caught by useStaffViewing's impersonation
       * lookup, which the database enforces on its own.
       */
      const ownerId = resolvedProfile?.user_id ?? null;
      if (adminRow && ownerId && usableSession.user.id === ownerId && adminRow.id === ownerId) {
        setForwardedAdmin(NO_FORWARDED_ADMIN);
      }

      // Every new creator arrives without a `bio_settings` row — 129 of 1,262
      // production profiles have none — and without one AMA, ShoutOut Card,
      // Newsletter and Publish are all permanently disabled. Live creates it on
      // every dashboard load; this creates it once, here, so all three My Page
      // tabs come alive together and no screen has to special-case its absence.
      if (resolvedProfile) await ensureBioSettings(resolvedProfile);

      setStatus('ready');
    } catch (e: any) {
      setError(e?.message || 'Could not load your creator account.');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    void bootstrap();
  }, [bootstrap]);

  const refresh = useCallback(async () => {
    if (!profile?.id) return;
    const { data } = await supabase
      .from('talent_profiles')
      .select('*')
      .eq('id', profile.id)
      .single();
    if (data) {
      setProfile(data as any);
      setColumnsAreFull(true);
    }
  }, [profile?.id]);

  const patchProfile = useCallback((patch: Partial<TalentProfileRow>) => {
    // Always a new object. The current dashboard mutates the profile in place
    // in a couple of places, which renders nothing until an unrelated fetch
    // happens to trigger one.
    setProfile((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo<CreatorSessionState>(
    () => ({
      status,
      profile,
      user,
      talentId: profile?.id ?? null,
      forwardedAdmin,
      columnsAreFull,
      loginEmail,
      error,
      refresh,
      patchProfile,
    }),
    [
      status,
      profile,
      user,
      forwardedAdmin,
      columnsAreFull,
      loginEmail,
      error,
      refresh,
      patchProfile,
    ]
  );

  return (
    <CreatorSessionContext.Provider value={value}>{children}</CreatorSessionContext.Provider>
  );
}

export function useCreatorSession(): CreatorSessionState {
  const ctx = useContext(CreatorSessionContext);
  if (!ctx) {
    throw new Error('useCreatorSession must be used inside <CreatorSessionProvider>');
  }
  return ctx;
}
